import { differenceInDays } from 'date-fns';
import React from 'react';
import { useLocalStorage } from 'react-use';
import styled, { keyframes } from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { Shadows } from '../../../ui/Standards';
import { TextButton } from '../../../ui/TextButton';
import { FadeIn } from '../../../ui/Transitions';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { PopupManager } from '../../popups/PopupManager';
import { AccountModel } from '../AccountModel';

const Over = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, .5);
    border-radius: .75rem;
    overflow: hidden;
    opacity: 0;
    transition: opacity .2s ease-in-out;
`;

const pulseAnim = (color: string) => keyframes`
    0% {
        opacity: 1;
        box-shadow: 0 0 0 0 ${color};
        border-radius: .75rem;
    }
    50%, 100% {
        opacity: 0;
        box-shadow: 0 0 0 2rem ${color};
        border-radius: 1rem;
    }
`;

const Pulser = styled.div`
    position: absolute;
    inset: 0;
`;

const Frame = styled.div`
    position: relative;
    margin: 0 1rem;
    cursor: pointer;

    img {
        display: block;
    border-radius: .75rem;
    box-shadow: ${Shadows.lg};
    }

    &:hover {
        ${Over} {
            opacity: 1;
        }
    }

    ${Pulser} {
        animation: ${p => pulseAnim(Colors.primary)} 3s infinite;
        animation-delay: 3s;
    }
`;

const Doodle = styled.div`
    padding: 1rem 0 3rem;
    font-family: 'Caveat';
    font-weight: bold;
    font-size: 1.5rem;
    line-height: 1.15em;
    color: ${Colors.textMenu};
    transform: rotate(-5deg);
    user-select: none;
    text-align: center;
    pointer-events: none;

    svg {
        position: absolute;
        left: 50%;
        top: 3rem;
        width: 1.5rem;
        margin-left: 1rem;
        transform: scale(1, -1) rotate(-55deg);
    }
`;

const Pulse = () => {
    const { current } = useObserver(useResolve(PopupManager));
    if (!!current) return null;
    return <Pulser />;
};

export const HelpVideoBlock = () => {

    const [clicked, setClicked] = useLocalStorage('deqo/ui/migrateHelp2024Seen', false);
    const [hidden, setHidden] = useLocalStorage('deqo/ui/migrateHelp2024Hidden', false);
    const account = useResolve(AccountModel);

    // Only show for one month after migrate date
    const md = account.metadata.migrateDate ?? null;
    if (!md) return null;
    if (md && Math.abs(differenceInDays(new Date(), new Date(md))) > 30) return null;

    // If hidden, don't show
    if (hidden) return null;

    return <FadeIn after="1s">
        <Flexer relative>
            <Doodle>
                <span>New Dashboard Tour!!</span>
                <svg viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.88672 1.13867C7.45625 2.92524 5.82245 7.46083 2.73104 11.3106" stroke={Colors.textMenu} strokeLinecap="round" />
                    <path d="M8.94489 2.82052L7.8859 1.13898L5.82264 1.92653" stroke={Colors.textMenu} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </Doodle>
            <Frame>
                <a href="https://www.tella.tv/video/metro-retro-dashboard-walkthrough-bsit" target="_blank" rel="noreferrer" onClick={() => setClicked(true)}>
                    <img src="https://www.tella.tv/api/stories/clzr7l54r000i09ihb7zk3ckn/thumb.jpg?version=2024-08-12T17:39:26.003Z&resolution=1280x720" />
                    <Over>
                        <Icon size="4" src="deqo://icons/i8/play-fill" color="white" />
                    </Over>
                    {!clicked && <Pulse />}
                </a>
            </Frame>
            <Flexer flex="row" center padding="1 0 0 0">
                <TextButton size="0.75" onClick={() => setHidden(true)}>Dismiss</TextButton>
            </Flexer>
        </Flexer>
    </FadeIn>;
};
